import { useCallback } from 'react';
import { _submissionMath } from '../utils/mathInstance';
import { formatNumberWithDecimal, removeThousandSeparator } from '../utils/formUtils';
import { tryParseDate, diffInDays, isBefore, parseTime24 } from '../../../utils/dateUtils';
import logger from '../../../utils/logger';

/**
 * useFormCalculations
 *
 * Encapsulates every calculation engine that was previously inlined in
 * SubmissionDynamicform. Extracted to reduce the component from a God
 * Component to a thin orchestrator.
 *
 * Design principles:
 * - All functions accept formArray as an explicit parameter, never closing
 *   over stale component state.
 * - formArrayRef.current is used only as a fallback when a caller does not
 *   pass the array (backwards-compatible call sites).
 * - setArray / setValue / getValues are the only component-level deps; all
 *   are guaranteed stable (React never changes setState refs).
 *
 * @param {Object} params
 * @param {React.MutableRefObject} params.formArrayRef  - ref always pointing to latest formArray
 * @param {Function} params.setArray                    - formArray state setter
 * @param {Function} params.getValues                   - RHF getValues
 * @param {Function} params.setValue                    - RHF setValue
 */
export function useFormCalculations({ formArrayRef, setArray, getValues, setValue }) {

  // ─── Grid column sum ──────────────────────────────────────────────────────

  const calculateGridColumnSum = useCallback((gridName, columnName, resolvedFormArray) => {
    const grid = resolvedFormArray.find(
      (f) => f.name === gridName && f.type === 'GridComponent'
    );
    if (!grid || !Array.isArray(grid.gridRows)) return 0;

    return grid.gridRows.reduce((total, row) => {
      const cellValue = row[columnName];
      if (cellValue !== undefined && cellValue !== null && cellValue !== '') {
        const numValue = Number(String(cellValue).replace(/,/g, ''));
        return total + (isNaN(numValue) ? 0 : numValue);
      }
      return total;
    }, 0);
  }, []);

  // ─── Equation solver ──────────────────────────────────────────────────────

  const equationSolver = useCallback(async (rule, localFormArray) => {
    const resolvedFormArray = localFormArray ?? formArrayRef.current;
    const objects = {};
    const useGridSum = rule.isGridColumnSumUsed === true && rule.parameterDetails;

    const resolveField = (fieldName) => {
      const field    = resolvedFormArray.find((f) => f.name === fieldName);
      // NDVIAnalyzer checked as its own branch (not merged with Map's, kept
      // isolated per prior instruction) even though it reads the same `.area`.
      const rawValue = field?.type === 'Map' ? field.area
        : field?.type === 'NDVIAnalyzer' ? field.area
        : getValues(fieldName);
      if (typeof rawValue !== 'undefined' && rawValue !== '') {
        objects[fieldName] = Number(removeThousandSeparator(rawValue));
      } else {
        objects[fieldName] = 0;
      }
    };

    if (useGridSum) {
      for (const param of rule.parameterDetails) {
        if (param.type === 'gridColumnSum') {
          objects[param.columnName] = calculateGridColumnSum(
            param.gridName, param.columnName, resolvedFormArray
          );
        } else {
          resolveField(param.name);
        }
      }
    } else {
      for (const fieldName of rule.list) {
        resolveField(fieldName);
      }
    }

    try {
      return _submissionMath.evaluate(rule.expression, objects);
    } catch {
      return 0;
    }
  }, [calculateGridColumnSum, getValues, formArrayRef]);

  // ─── Date equation solver ─────────────────────────────────────────────────

  const equationDateSolver = useCallback((calcRule, resolvedFormArray) => {
    try {
      const values = {};

      for (const fieldName of calcRule.list) {
        const field = resolvedFormArray.find((f) => f.name === fieldName);
        if (!field) { values[fieldName] = 0; continue; }

        if (field.type === 'Map') {
          const areaValue = parseFloat(field.area);
          values[fieldName] = isNaN(areaValue) ? 0 : areaValue;
          continue;
        }
        // Independent copy of the "Map" branch above, kept isolated from Map.
        if (field.type === 'NDVIAnalyzer') {
          const areaValue = parseFloat(field.area);
          values[fieldName] = isNaN(areaValue) ? 0 : areaValue;
          continue;
        }
        if (field.type === 'Number' || field.type === 'CalculatedField') {
          values[fieldName] = parseFloat(getValues(fieldName) || 0);
          continue;
        }
        // A plain numeric string (e.g. a Text field autofilled with "7490") is a
        // number — new Date("7490") would otherwise parse it as the year 7490.
        const rawValue = getValues(fieldName);
        const numericValue = typeof rawValue === 'string' && rawValue.trim() !== ''
          ? Number(removeThousandSeparator(rawValue))
          : NaN;
        values[fieldName] = !isNaN(numericValue) ? numericValue : tryParseDate(rawValue);
      }

      const expression = calcRule.expression;
      if (expression.includes('-')) {
        const [endKey, startKey] = expression.split('-').map((v) => v.trim());
        const start = values[startKey];
        const end   = values[endKey];
        if (start instanceof Date && end instanceof Date) {
          if (isBefore(end, start)) {
            return NaN; // caller handles user-visible error
          }
          return diffInDays(start, end);
        }
      }

      let finalExpression = expression;
      Object.keys(values).forEach((key) => {
        finalExpression = finalExpression.replaceAll(key, values[key]);
      });
      const result = _submissionMath.evaluate(finalExpression);
      return Number(result.toFixed(3));
    } catch {
      return NaN;
    }
  }, [getValues]);

  // ─── Time equation solver ─────────────────────────────────────────────────

  const equationTimeSolver = useCallback((calcRule) => {
    try {
      const objects = {};

      for (const fieldName of calcRule.list) {
        const rawValue = getValues(fieldName);
        if (!rawValue) { objects[fieldName] = null; continue; }
        const timeValue = parseTime24(rawValue);
        objects[fieldName] = timeValue ? timeValue.totalMinutes : null;
      }

      if (calcRule.expression.includes('-')) {
        const [endKey, startKey] = calcRule.expression.split('-').map((s) => s.trim());
        const startMinutes = objects[startKey];
        const endMinutes   = objects[endKey];
        if (startMinutes == null || endMinutes == null) return 0;
        let diffMinutes = endMinutes - startMinutes;
        if (diffMinutes < 0) diffMinutes += 24 * 60;
        const hours   = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        return `${hours}h ${minutes}m`;
      }
      return 0;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        logger.error('equationTimeSolver failed:', error);
      }
      return 0;
    }
  }, [getValues]);

  // ─── onChangeCalculation ──────────────────────────────────────────────────

  // Mutual recursion: onChangeCalculation ↔ recalculate ↔ reAggregationCalculate.
  // All three circular deps are themselves stable (setArray/setValue/getValues are
  // React-guaranteed stable). ESLint cannot prove this statically, hence disable.
  const onChangeCalculationRef = useCallback(
    async function onChangeCalculation(calcrule, formArray) {
      if (!calcrule?.length) return;

      for (const rule of calcrule) {
        const binding_variable = rule.binding_variable;
        const solution         = await equationSolver(rule, formArray);

        if (!isNaN(solution)) {
          const field        = formArray.find((item) => item.name === binding_variable);
          let decimalValue   = null;
          if (field?.isDecimalPoint) {
            decimalValue = field.hasOwnProperty('decimalValue')
              ? parseInt(field.decimalValue, 10) || null
              : 2;
          }
          const calculatedValue = formatNumberWithDecimal(solution, decimalValue);
          setValue(binding_variable, calculatedValue);

          for (const item of formArray) {
            if (item.name === binding_variable) item.value = calculatedValue;
          }
        }

        // Downstream chain — share same mutable formArray reference to avoid
        // intermediate setArray renders per calc-rule iteration.
        recalculateRef(binding_variable, formArray);
        reAggregationCalculateRef(binding_variable, formArray);

        const calcField = formArray.find((d) => d.name === binding_variable);
        if (calcField?.calc_rule?.length > 0) {
          await onChangeCalculationRef(calcField.calc_rule, formArray);
        }
      }
    },
    [equationSolver, setValue] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ─── recalculate (sum field → calculated form) ────────────────────────────

  const recalculateRef = useCallback(
    async function recalculate(binding_variable, formArray) {
      const parentSumField = formArray.find((d) => d.name === binding_variable);
      if (!parentSumField) return;

      const totalCalcId = parentSumField.total_calc_id;
      const allFields   = formArray.filter(
        (d) => d.calc_field === true && d.total_calc === true && d.total_calc_id === totalCalcId
      );
      if (allFields.length === 0) return;

      const bindingVar   = allFields[0].total_binding_var;
      const totalValues  = allFields.map((f) => Number(removeThousandSeparator(getValues(f.name))));
      const allTotal     = totalValues.reduce((a, b) => a + b, 0);

      if (!isNaN(allTotal)) {
        const decimalField  = formArray.find((f) => f.name === bindingVar);
        const decimalPlaces = decimalField?.isDecimalPoint
          ? (decimalField.hasOwnProperty('decimalValue') ? decimalField.decimalValue : 2)
          : null;
        const convertedValue = formatNumberWithDecimal(allTotal, decimalPlaces);
        setValue(bindingVar, convertedValue);
        for (const item of formArray) {
          if (item.name === bindingVar) item.value = convertedValue;
        }
      }

      const calcField = formArray.find((d) => d.name === bindingVar);
      if (calcField?.calc_rule?.length > 0) {
        await onChangeCalculationRef(calcField.calc_rule, formArray);
      }
      setArray(formArray);
    },
    [getValues, setValue, setArray, onChangeCalculationRef]
  );

  // ─── triggerTotalCalculation (normal number field sum) ────────────────────

  const triggerTotalCalculation = useCallback(
    async (rules, array) => {
      if (!rules) return;
      const currentFormArray = array ?? formArrayRef.current;

      const filteredForm = currentFormArray.filter(
        (d) => d.total_calc_id === rules.total_calc_id
      );

      const valuesArray = filteredForm
        .map((f) => getValues(f.name))
        .filter((v) => v !== undefined && v !== null)
        .map((v) => removeThousandSeparator(v));

      const sum = valuesArray.reduce(
        (acc, cur) => Number(acc) + Number(cur), 0
      );

      if (!isNaN(sum)) {
        const decimalField  = currentFormArray.find((f) => f.name === rules.total_binding_var);
        const decimalPlaces = decimalField?.isDecimalPoint
          ? (decimalField.hasOwnProperty('decimalValue') ? decimalField.decimalValue : 2)
          : null;
        const convertedValue = formatNumberWithDecimal(sum, decimalPlaces);
        setValue(rules.total_binding_var, convertedValue);
        for (const item of currentFormArray) {
          if (item.name === rules.total_binding_var) item.value = convertedValue;
        }
      }

      // Chain into any calc_rule depending on this sum field
      const calcField = currentFormArray.find((d) => d.name === rules.total_binding_var);
      if (calcField?.calc_rule?.length > 0) {
        for (const calcRule of calcField.calc_rule) {
          const solution = await equationSolver(calcRule, currentFormArray);
          if (!isNaN(solution)) {
            const decimalField  = currentFormArray.find((f) => f.name === rules.total_binding_var);
            const decimalPlaces = decimalField?.isDecimalPoint
              ? (decimalField.hasOwnProperty('decimalValue') ? decimalField.decimalValue : 2)
              : null;
            const convertedValue = formatNumberWithDecimal(solution, decimalPlaces);
            setValue(calcRule.binding_variable, convertedValue);
            for (const item of currentFormArray) {
              if (item.name === calcRule.binding_variable) item.value = convertedValue;
            }
          }
        }
      }
      setArray(currentFormArray);
    },
    [getValues, setValue, setArray, equationSolver, formArrayRef]
  );

  // ─── triggerCountValueCalculation ─────────────────────────────────────────

  const triggerCountValueCalculation = useCallback(
    async (array) => {
      const list = { array: [...array] };
      array?.forEach((obj) => {
        if (obj?.dummyAggregationRules?.aggregationType !== 'CountValue') return;
        const groupId       = obj.group_id;
        const filteredArray = array.filter(
          (item) => item.group_id === groupId && item.group_first === true
        );
        const count = filteredArray.length;
        if (!isNaN(count)) {
          setValue(obj.name, count);
          for (const item of list.array) {
            if (item.name === obj.name) item.value = count;
          }
        }
      });
      setArray(list.array);
    },
    [setValue, setArray]
  );

  // ─── aggregation calculation helper ───────────────────────────────────────

  function computeAggregation(aggregationType, values) {
    if (!values.length) return null;
    const nums = values.map(Number);
    const sum  = nums.reduce((a, b) => a + b, 0);
    switch (aggregationType) {
      case 'SumValue':     return sum;
      case 'AverageValue': return sum / nums.length;
      case 'MinValue':     return Math.min(...nums);
      case 'MaxValue':     return Math.max(...nums);
      default:             return null;
    }
  }

  // ─── triggerAggregationTypeCalculation ────────────────────────────────────

  const triggerAggregationTypeCalculation = useCallback(
    async (rulesArray, array) => {
      if (!Array.isArray(rulesArray)) return;
      const currentFormArray = array ?? formArrayRef.current;

      for (const rules of rulesArray) {
        const filteredForm = currentFormArray.filter((d) =>
          d?.aggregation_rules?.some((r) => r.aggregation_id === rules.aggregation_id)
        );
        const list = { array: [...currentFormArray] };

        const valuesArray = filteredForm
          .map((f) => getValues(f.name))
          .filter((v) => v !== undefined && v !== null && v !== '')
          .map((v) => removeThousandSeparator(v));

        const AGGREGATION_TYPES = ['SumValue', 'AverageValue', 'MinValue', 'MaxValue'];
        if (AGGREGATION_TYPES.includes(rules.aggregationType)) {
          const result         = computeAggregation(rules.aggregationType, valuesArray);
          const convertedValue = result !== null ? formatNumberWithDecimal(result, 2) : '';
          setValue(rules.aggregation_binding_var, convertedValue);
          for (const item of list.array) {
            if (item.name === rules.aggregation_binding_var) item.value = convertedValue;
          }
        }

        // Chain downstream calc_rules
        const calcField = currentFormArray.find((d) => d.name === rules.aggregation_binding_var);
        if (calcField?.calc_rule?.length > 0) {
          for (const calcRule of calcField.calc_rule) {
            const solution = await equationSolver(calcRule, currentFormArray);
            if (!isNaN(solution)) {
              const convertedValue = formatNumberWithDecimal(solution, 2);
              setValue(calcRule.binding_variable, convertedValue);
              for (const item of list.array) {
                if (item.name === calcRule.binding_variable) item.value = convertedValue;
              }
            }
          }
        }
        setArray([...list.array]);
      }
    },
    [getValues, setValue, setArray, equationSolver, formArrayRef]
  );

  // ─── reAggregationCalculate ───────────────────────────────────────────────

  const reAggregationCalculateRef = useCallback(
    async function reAggregationCalculate(binding_variable, formArray) {
      const parentField = formArray.find((d) => d.name === binding_variable);
      const aggreRules  = parentField?.aggregationRule;
      if (!Array.isArray(aggreRules) || aggreRules.length === 0) return;

      for (const aggreRule of aggreRules) {
        const allFields = formArray.filter((d) =>
          d?.aggregation_rules?.some(
            (r) => d.calc_field === true &&
                   d.aggregation_flag === true &&
                   r.aggregation_id === aggreRule.aggregation_id
          )
        );
        if (allFields.length === 0) continue;
        const list = { formArray };

        const aggregatorValues = allFields.map((f) =>
          Number(removeThousandSeparator(getValues(f.name)))
        );

        const AGGREGATION_TYPES = ['SumValue', 'AverageValue', 'MinValue', 'MaxValue'];
        if (AGGREGATION_TYPES.includes(aggreRule.aggregationType)) {
          const result         = computeAggregation(aggreRule.aggregationType, aggregatorValues);
          const convertedValue = result !== null ? formatNumberWithDecimal(result, 2) : null;
          setValue(aggreRule.aggregation_binding_var, convertedValue);
          for (const item of list.formArray) {
            if (item.name === aggreRule.aggregation_binding_var) item.value = convertedValue;
          }
        }

        const calcField = formArray.find((d) => d.name === aggreRule.aggregation_binding_var);
        if (calcField?.calc_rule?.length > 0) {
          await onChangeCalculationRef(calcField.calc_rule, formArray);
        }
        setArray(list.formArray);
      }
    },
    [getValues, setValue, setArray, onChangeCalculationRef]
  );

  // ─── onChangeDateCalc ─────────────────────────────────────────────────────

  const onChangeDateCalc = useCallback(
    async (calcRule, formArray) => {
      if (!calcRule?.length) return;
      const fieldByName = new Map(formArray.map((f) => [f.name, f]));

      for (const rule of calcRule) {
        const binding_variable = rule.binding_variable;
        const solution         = equationDateSolver(rule, formArray);

        if (!isNaN(solution)) {
          const field       = fieldByName.get(binding_variable);
          let decimalValue  = null;
          if (field?.isDecimalPoint) {
            decimalValue = field.hasOwnProperty('decimalValue')
              ? parseInt(field.decimalValue, 10) || null
              : 2;
          }
          const calculatedValue = formatNumberWithDecimal(solution, decimalValue);
          setValue(binding_variable, calculatedValue);
          if (field) field.value = calculatedValue;
        }

        await recalculateRef(binding_variable, formArray);
        await reAggregationCalculateRef(binding_variable, formArray);

        const calcField = fieldByName.get(binding_variable);
        if (calcField?.calc_rule?.length > 0) {
          await onChangeCalculationRef(calcField.calc_rule, formArray);
        }
      }
      setArray(formArray);
    },
    [equationDateSolver, recalculateRef, reAggregationCalculateRef, onChangeCalculationRef, setValue, setArray]
  );

  // ─── onChangeTime ─────────────────────────────────────────────────────────

  const onChangeTime = useCallback(
    (calcRule, formArray) => {
      if (!calcRule?.length) return;
      for (const rule of calcRule) {
        const calculatedValue = equationTimeSolver(rule);
        setValue(rule.binding_variable, calculatedValue);
        for (const item of formArray) {
          if (item.name === rule.binding_variable) item.value = calculatedValue;
        }
      }
      setArray(formArray);
    },
    [equationTimeSolver, setValue, setArray]
  );

  // ─── triggerGridSumCalculations ───────────────────────────────────────────

  const triggerGridSumCalculations = useCallback(
    async (gridName, formArray) => {
      for (const field of formArray) {
        if (!field.calc_rule?.length) continue;
        for (const rule of field.calc_rule) {
          if (rule.isGridColumnSumUsed && rule.parameterDetails) {
            const usesThisGrid = rule.parameterDetails.some(
              (p) => p.type === 'gridColumnSum' && p.gridName === gridName
            );
            if (usesThisGrid) {
              await onChangeCalculationRef(field.calc_rule, formArray);
              break;
            }
          }
        }
      }

      const grid = formArray.find((f) => f.name === gridName);
      if (grid?.columnData) {
        for (const col of grid.columnData) {
          if (!col.calc_rule?.length) continue;
          for (const rule of col.calc_rule) {
            if (rule.isGridColumnSumUsed && rule.parameterDetails) {
              const usesThisGrid = rule.parameterDetails.some(
                (p) => p.type === 'gridColumnSum' && p.gridName === gridName
              );
              if (usesThisGrid) {
                const targetField = formArray.find((f) => f.name === rule.binding_variable);
                if (targetField?.calc_rule) {
                  await onChangeCalculationRef([rule], formArray);
                }
                break;
              }
            }
          }
        }
      }
    },
    [onChangeCalculationRef]
  );

  // ─── Public API ───────────────────────────────────────────────────────────

  return {
    onChangeCalculation:             onChangeCalculationRef,
    recalculate:                     recalculateRef,
    reAggregationCalculate:          reAggregationCalculateRef,
    equationSolver,
    equationDateSolver,
    equationTimeSolver,
    calculateGridColumnSum,
    triggerGridSumCalculations,
    triggerTotalCalculation,
    triggerCountValueCalculation,
    triggerAggregationTypeCalculation,
    onChangeDateCalc,
    onChangeTime,
  };
}
